<script setup lang="ts">
const { open: openIntentModal } = useIntentModal()

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <section class="relative bg-white overflow-hidden">
    <div class="mx-auto max-w-7xl">
      <div
        class="relative z-10 bg-white pb-8 sm:pb-16 md:pb-20 lg:w-full lg:max-w-2xl lg:pb-28 xl:pb-32"
      >
        <!-- Diagonal clip for large screens -->
        <svg
          class="absolute inset-y-0 right-0 hidden h-full w-48 translate-x-1/2 transform text-white lg:block"
          fill="currentColor"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polygon points="50,0 100,0 50,100 0,100" />
        </svg>

        <div class="px-6 pt-10 sm:px-8 sm:pt-12 lg:px-12 lg:pt-16 xl:pt-20">
          <!-- Trust badge -->
          <div class="mb-6">
            <span
              class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700"
            >
              <span class="relative flex h-2 w-2">
                <span
                  class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex h-2 w-2 rounded-full bg-primary-500"
                ></span>
              </span>
              Supporting 100+ sites across Australia
            </span>
          </div>

          <h1 class="text-4xl font-bold tracking-tight text-dark sm:text-5xl md:text-6xl">
            <span class="block">Your Single Partner for</span>
            <span class="block text-primary-400">Security, Connectivity &amp; IT</span>
          </h1>

          <p
            class="mt-6 max-w-md text-lg text-neutral-600 sm:text-xl md:mt-8 md:max-w-xl"
          >
            Integrated solutions for businesses that demand reliability and
            accountability. No more juggling vendors or firefighting IT issues.
          </p>

          <!-- CTA Buttons -->
          <div class="mt-8 flex flex-col gap-4 sm:flex-row sm:gap-4 md:mt-10">
            <Button size="lg" class="text-base px-8 py-6" @click="openIntentModal('free-quote')">
              Get a Free Quote
            </Button>
            <Button
              variant="outline"
              size="lg"
              class="text-base px-8 py-6 border-dark text-dark hover:bg-dark hover:text-white"
              @click="openIntentModal('consultation')"
            >
              Book a Consultation
            </Button>
          </div>

          <!-- Quick problem links -->
          <div class="mt-8 flex flex-wrap gap-2">
            <button
              class="group inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-light px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
              @click="scrollToSection('connectivity')"
            >
              <Icon name="lucide:wifi-off" class="h-4 w-4" />
              Internet keeps dropping
              <Icon
                name="lucide:arrow-down"
                class="h-3 w-3 transition-transform group-hover:translate-y-0.5"
              />
            </button>
            <button
              class="group inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-light px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
              @click="scrollToSection('security')"
            >
              <Icon name="lucide:shield-off" class="h-4 w-4" />
              Security has gaps
              <Icon
                name="lucide:arrow-down"
                class="h-3 w-3 transition-transform group-hover:translate-y-0.5"
              />
            </button>
            <button
              class="group inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-light px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
              @click="scrollToSection('itcloud')"
            >
              <Icon name="lucide:flame" class="h-4 w-4" />
              Stuck firefighting IT
              <Icon
                name="lucide:arrow-down"
                class="h-3 w-3 transition-transform group-hover:translate-y-0.5"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Hero image (right side) -->
    <div class="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
      <div
        class="h-56 w-full bg-linear-to-br from-dark to-neutral-800 sm:h-72 md:h-96 lg:h-full"
      >
        <!-- Placeholder pattern - replace with actual image -->
        <div class="relative h-full w-full overflow-hidden">
          <!-- Grid pattern overlay -->
          <div class="absolute inset-0 opacity-10">
            <svg class="h-full w-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="white"
                    stroke-width="1"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <!-- Decorative elements -->
          <div
            class="absolute right-10 top-10 h-20 w-20 rounded-full bg-primary-400/20 blur-xl"
          ></div>
          <div
            class="absolute bottom-20 left-10 h-32 w-32 rounded-full bg-accent-500/20 blur-2xl"
          ></div>

          <!-- Icon composition -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="grid grid-cols-3 gap-8 p-8">
              <div
                class="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
              >
                <Icon name="lucide:wifi" class="h-10 w-10 text-primary-400" />
              </div>
              <div
                class="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
              >
                <Icon name="lucide:shield-check" class="h-10 w-10 text-accent-400" />
              </div>
              <div
                class="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
              >
                <Icon name="lucide:cloud" class="h-10 w-10 text-primary-400" />
              </div>
            </div>
          </div>

          <!-- Bottom gradient fade -->
          <div
            class="absolute inset-x-0 bottom-0 h-32 bg-linear-to-t from-dark/50 to-transparent"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>
